import { useMemo, useState } from 'react';
import styled from '@emotion/styled';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setScheduleData } from '@/redux/slices/focusNewScheduleSlice';
import ScheduleSelectedAccordion from '@/components/Focus/AvailsSchedule/NewSchedule/ScheduleSelectedAccordion';
import NewScheduleAvailItem from '../NewScheduleAvails/NewScheduleAvailItem';
import { selectAllLanguagesMap } from '@/redux/selectors/constantsSelectors';
import { selectFocusNewScheduleSlice } from '@/redux/selectors/focusNewScheduleSelectors';

type ScheduleSelectedLanguageProps = {
  type: 'audio' | 'subtitle';
  selectedDubs?: string[];
  selectedSubs?: string[];
  readOnly?: boolean;
};

const ScheduleSelectedLanguage = ({ type, selectedDubs = [], selectedSubs = [], readOnly = false }: ScheduleSelectedLanguageProps) => {
  const { rightsPossible } = useAppSelector(selectFocusNewScheduleSlice);
  const dispatch = useAppDispatch();
  const allLanguagesMap = useAppSelector(selectAllLanguagesMap);
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedCount = type === 'audio' ? selectedDubs.length : selectedSubs.length;
  const languages = type === 'audio' ? selectedDubs : selectedSubs;

  const dubRights = useMemo(() => {
    if (!rightsPossible) return [];

    return Array.from(
      new Set(
        Object.values(rightsPossible.territories)
          .flatMap((territory) => territory.audio)
          .map((item) => item.language)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rightsPossible]);

  const subRights = useMemo(() => {
    if (!rightsPossible) return [];

    return Array.from(
      new Set(
        Object.values(rightsPossible.territories)
          .flatMap((territory) => territory.timedtext)
          .map((item) => item.language)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rightsPossible]);

  const rights = type === 'audio' ? dubRights : subRights;

  const removeLanguage = (language: string) => {
    dispatch(
      setScheduleData(
        type === 'audio' ? { selectedDubs: languages.filter((l) => l !== language) } : { selectedSubs: languages.filter((l) => l !== language) }
      )
    );
  };

  const formatDisplayLabel = useMemo(() => {
    return (code: string) => {
      const label = allLanguagesMap?.[code.replace('-', '_')]?.label;
      return `${label ?? code} [${code}]`;
    };
  }, [allLanguagesMap]);

  return (
    <ScheduleSelectedAccordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
      <ScheduleSelectedAccordion.Summary>
        <ScheduleSelectedAccordion.Content>
          <ScheduleSelectedAccordion.Title>{type === 'audio' ? 'Audio' : 'Subtitle'}</ScheduleSelectedAccordion.Title>
          <ScheduleSelectedAccordion.Caption>
            {selectedCount} {selectedCount > 1 ? 'languages' : 'language'} selected
          </ScheduleSelectedAccordion.Caption>
        </ScheduleSelectedAccordion.Content>
      </ScheduleSelectedAccordion.Summary>
      <ScheduleSelectedAccordion.Details>
        <Languages>
          {languages.map((language) => (
            <LanguageItem key={language}>
              <NewScheduleAvailItem
                isSelectedItem={false}
                primaryLabel={formatDisplayLabel(language)}
                onClick={readOnly ? undefined : () => removeLanguage(language)}
                hasRights={rights.includes(language)}
                disabled={readOnly}
              />
            </LanguageItem>
          ))}
        </Languages>
      </ScheduleSelectedAccordion.Details>
    </ScheduleSelectedAccordion>
  );
};

export default ScheduleSelectedLanguage;

const Languages = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(1),
  width: '100%'
}));

const LanguageItem = styled('div')({
  minWidth: 0
});
